import styled, { css } from 'styled-components';
import { ToggleChip } from '@interstate/components/Chip';
import { Typography } from '@interstate/components/Typography';

export const LoadingIndicatorWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    #ids-spinner-root {
        margin-bottom: 20px;
    }
`;

export const ListWithOverlayWrapper = styled.div`
    position: relative;
`;

export const RecalcOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.6);
    z-index: 2;
`;

export const SelectActionChip = styled(ToggleChip)<{ isApplied: boolean; isApplying?: boolean }>`
    border-radius: 714.214rem !important;
    min-width: 2.857rem;
    border: 0.0714rem solid;
    height: 1.428rem !important;
    width: 4.214rem;
    background-color: #fff !important;
    color: #000000 !important;
    .MuiChip-label {
        padding: 0 !important;
    }

    ${({ isApplied }) =>
        isApplied &&
        css`
            background-color: rgba(0, 52, 104, 1) !important;
            color: #fff !important;
            width: 5.29rem !important;
        `};

    ${({ isApplying }) =>
        isApplying &&
        css`
            width: 5.6rem !important;
        `};
`;

const GRID_SCROLLBAR_HEIGHT = '8px';
const GRID_SCROLLBAR_THUMB_COLOR = '#b0bfd0';
const GRID_SCROLLBAR_TRACK_COLOR = '#eef2f7';

const FROZEN_DIVIDER_WIDTH = '1px';
const FROZEN_DIVIDER_COLOR = '#c7d4e2';
const FROZEN_DIVIDER_SHADOW_WIDTH = '6px';
const FROZEN_DIVIDER_SHADOW_COLOR = 'rgba(0, 52, 104, 0.18)';
const FROZEN_DIVIDER_CONTENT_GAP = '12px';

const OUTER_GRID_SCROLL_CONTAINER = ".ant-table-content:not([data-testid='ids-table-incentives-table'] .ant-table-content)";

export const TableWrapperForDealCentral = styled.div<{
    $appliedProgramKey: string;
    $frozenColumnsWidth?: string;
    $frozenColumnsEnabled?: boolean;
}>`
    ${({ $frozenColumnsEnabled }) =>
        $frozenColumnsEnabled
            ? css`
                  isolation: isolate;

                  ${OUTER_GRID_SCROLL_CONTAINER} {
                      scrollbar-width: thin;
                      scrollbar-color: ${GRID_SCROLLBAR_THUMB_COLOR} ${GRID_SCROLLBAR_TRACK_COLOR};
                  }

                  ${OUTER_GRID_SCROLL_CONTAINER}::-webkit-scrollbar {
                      height: ${GRID_SCROLLBAR_HEIGHT};
                      -webkit-appearance: none;
                  }

                  ${OUTER_GRID_SCROLL_CONTAINER}::-webkit-scrollbar-track {
                      background: ${GRID_SCROLLBAR_TRACK_COLOR};
                      border-radius: ${GRID_SCROLLBAR_HEIGHT};
                  }

                  ${OUTER_GRID_SCROLL_CONTAINER}::-webkit-scrollbar-thumb {
                      background: ${GRID_SCROLLBAR_THUMB_COLOR};
                      border-radius: ${GRID_SCROLLBAR_HEIGHT};
                  }
              `
            : ''}

    .ant-table-expand-icon-col {
        width: 0 !important;
    }

    .ant-table {
        background: transparent;
    }

    .ant-table-container,
    .ant-table-content {
        border: none !important;
    }

    .ant-table-cell-fix-left,
    .ant-table-cell-fix-right,
    .ant-table-cell-fix-right::after,
    .ant-table-ping-right .ant-table-cell-fix-right-first::after {
        box-shadow: none !important;
        border-left: none !important;
        border-right: none !important;
    }

    .ant-table-thead > tr > th {
        background: #eef2f7 !important;
        color: #003468 !important;
        font-weight: 700 !important;
        border-bottom: 2px solid #003468 !important;
        border-left: none !important;
        border-right: none !important;
    }

    .ant-table-thead > tr > th::before {
        display: none !important;
    }

    .ant-table-thead > tr > th.ant-table-cell-fix-left-last,
    .ant-table-tbody > tr:not(.ant-table-measure-row) > td.ant-table-cell-fix-left-last {
        border-right: ${FROZEN_DIVIDER_WIDTH} solid ${FROZEN_DIVIDER_COLOR} !important;
    }

    ${({ $frozenColumnsWidth }) =>
        $frozenColumnsWidth
            ? css`
                  .ant-table-expanded-row-fixed {
                      box-sizing: border-box;
                      padding-left: calc(${$frozenColumnsWidth} + ${FROZEN_DIVIDER_CONTENT_GAP});
                  }

                  .ant-table-ping-left .ant-table-expanded-row-fixed::before {
                      content: '';
                      position: absolute;
                      top: 0;
                      bottom: 0;
                      left: ${$frozenColumnsWidth};
                      width: ${FROZEN_DIVIDER_SHADOW_WIDTH};
                      background: linear-gradient(to right, ${FROZEN_DIVIDER_SHADOW_COLOR}, transparent);
                      pointer-events: none;
                      z-index: 2;
                  }

                  .ant-table-expanded-row-fixed::after {
                      content: '';
                      position: absolute;
                      top: 0;
                      bottom: 0;
                      left: calc(${$frozenColumnsWidth} - ${FROZEN_DIVIDER_WIDTH});
                      width: ${FROZEN_DIVIDER_WIDTH};
                      background: ${FROZEN_DIVIDER_COLOR};
                      pointer-events: none;
                      z-index: 2;
                  }
              `
            : ''}

    .ant-table-tbody > tr:not(.ant-table-measure-row) > td {
        border-bottom: 1px solid #edf0f2 !important;
        border-top: none !important;
        border-left: none !important;
        border-right: none !important;
        padding-top: 0.85rem !important;
        padding-bottom: 0.85rem !important;
    }

    .ant-table-measure-row > td {
        padding: 0 !important;
        border: none !important;
        height: 0 !important;
    }

    [data-testid='ids-table-incentives-table'] {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;

        .ant-table,
        .ant-table-container,
        .ant-table-content,
        .ant-table-header,
        .ant-table-body {
            border: none !important;
            background: transparent !important;
        }

        .ant-table-thead > tr > th {
            background: transparent !important;
            color: #3a5673 !important;
            font-weight: 600 !important;
            letter-spacing: 0.02em !important;
            border-bottom: 1px solid #c7d4e2 !important;
        }

        .ant-table-cell-fix-left::after,
        .ant-table-cell-fix-right::after,
        .ant-table-cell.ant-table-cell-fix-right,
        .ant-table-cell.ant-table-cell-fix-left {
            box-shadow: none !important;
            border-left: none !important;
            border-right: none !important;
        }

        .ant-table-cell.ant-table-cell-fix-left,
        .ant-table-cell.ant-table-cell-fix-right {
            background: #ebf6ff !important;
        }

        .ant-table-cell-fix-left-last::after,
        .ant-table-cell-fix-right-first::after,
        .ant-table-ping-left .ant-table-cell-fix-left-last::after,
        .ant-table-ping-right .ant-table-cell-fix-right-first::after {
            box-shadow: none !important;
        }

        .ant-table-tbody > tr > td {
            border-left: none !important;
            border-right: none !important;
        }

        .ant-table-thead > tr > th.ant-table-cell-fix-left-last,
        .ant-table-tbody > tr:not(.ant-table-measure-row) > td.ant-table-cell-fix-left-last {
            border-right: none !important;
        }
    }

    .ant-table-expanded-row > .ant-table-cell {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        border-left: 4px solid #003468 !important;
    }

    .ant-table-expanded-row-fixed {
        border-left: none !important;
    }
    .ant-table-expanded-row-fixed.ant-table-expanded-row-fixed {
        background-color: #ebf6ff !important;
    }

    [data-row-key='${(props) => props.$appliedProgramKey}'] > .ant-table-row-expand-icon-cell {
        border-left: 4px solid #003468 !important;
    }

    [data-row-key='${(props) => props.$appliedProgramKey}'] > .ant-table-cell {
        background-color: #ebf6ff !important;
    }

    & table tr th:nth-of-type(2),
    & table tr th:last-of-type {
        pointer-events: none;

        svg {
            display: none;
        }
    }
`;
export const FilterDivider = styled.hr`
    display: block;
    width: 100%;
    border: none;
    border-top: 1px solid #cacfd9;
    margin: -0.5rem 0;
`;

export const FilterSectionTitle = styled(Typography)<{ $bold?: boolean; $enhanced?: boolean }>`
    display: block !important;
    margin-bottom: 0.75rem !important;

    ${({ $bold }) =>
        $bold &&
        css`
            font-weight: bold !important;
        `}

    ${({ $enhanced }) =>
        $enhanced &&
        css`
            font-family: 'Roboto Flex' !important;
            font-weight: 600 !important;
            font-size: var(--type-font-size-size-md, 16px) !important;
            line-height: 120% !important;
            letter-spacing: normal !important;
        `}
`;

export const FilterGroupTitle = styled(Typography)<{ $enhanced?: boolean }>`
    display: block !important;
    margin-bottom: 0.5rem !important;
    font-weight: bold !important;

    ${({ $enhanced }) =>
        $enhanced &&
        css`
            font-weight: var(--type-font-weight-weight-semibold, 600) !important;
            font-size: var(--type-font-size-size-sm, 14px) !important;
            line-height: 130% !important;
            letter-spacing: normal !important;
        `}
`;

export const FilterOptionList = styled.div<{ $gridLayout?: boolean; $enhanced?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    ${({ $gridLayout }) =>
        $gridLayout &&
        css`
            display: grid;
            grid-template-columns: repeat(2, 1fr);
        `}

    ${({ $enhanced }) =>
        $enhanced &&
        css`
            .MuiFormControlLabel-label {
                font-weight: var(--type-font-weight-weight-regular, 400) !important;
                font-size: var(--type-font-size-size-md, 16px) !important;
                line-height: 125% !important;
                letter-spacing: normal !important;
            }
        `}
`;
